import { useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";

/**
 * Composant de prise de photo directe (mobile & webcam) — utilise
 * `capture="environment"` pour ouvrir la caméra arrière sur mobile.
 * Uploade immédiatement l'image vers /api/uploads et remonte le chemin
 * de fichier au parent.
 */
export default function PhotoCapture({
  label,
  value,
  onChange,
  kind = "justificatif",
  testid,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const trigger = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ path: data.path, filename: file.name });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const reset = () => {
    onChange(null);
    setError("");
  };

  return (
    <div className="paper-card p-4 border border-slate-200" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-600">
        {label}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handle}
        className="hidden"
        data-testid={`${testid}-input`}
      />

      {value ? (
        <div className="mt-3 relative">
          <img
            src={fileUrl(value.path)}
            alt={value.filename}
            className="w-full h-48 object-cover rounded-sm border border-slate-200 bg-slate-100"
          />
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Photo enregistrée
            </span>
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-unisic-red"
              data-testid={`${testid}-retake`}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reprendre
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={trigger}
          disabled={uploading}
          className="mt-3 w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-unisic-gold px-4 py-8 rounded-sm text-slate-500 hover:text-unisic-blue transition-colors"
          data-testid={`${testid}-btn`}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 animate-spin text-unisic-gold" />
          ) : (
            <Camera className="w-6 h-6 text-unisic-gold" />
          )}
          <span className="text-sm font-medium">
            {uploading ? "Envoi en cours..." : "Prendre la photo"}
          </span>
          <span className="text-[11px] text-slate-400">
            Ouvre l'appareil photo (mobile) ou la webcam (ordinateur)
          </span>
        </button>
      )}

      {error && <div className="mt-2 text-xs text-unisic-red">{error}</div>}
    </div>
  );
}
